// Microbe RTS — the three clocks + match-end bookkeeping.
//
//   (1) sim time advances here.
//   (2) bot threat timer decays here (bot.ts fires + resets it).
//   (3) Great Oxygenation: at oxyIgniteAt, global O2 climbs; each Home erodes by
//       oxyBaseDps*(1-nodeShare)*O2 unless the side has Aerobic Respiration, which flips
//       erosion into +energy. A late ramp grows the dps to force a finish.
//   Domination hold is banked per side; the required continuous hold DECAYS from
//   dominationHoldMax down to dominationHoldFloor the more of the map you hold.

import type { GameState, Side } from '../types.js';
import { CLOCKS, WIN } from '../config.js';
import { nodeById, ownedCapturable, pushEvent } from './world.js';
import { isAerobic } from './units.js';

const DOM_THRESHOLD = WIN.dominationShare * CLOCKS.capturableNodeCount;

/** Continuous seconds a side must hold the domination threshold to win, given how many nodes it holds. */
function domRequired(heldCount: number): number {
  const denom = CLOCKS.capturableNodeCount - DOM_THRESHOLD;
  const overshoot = denom > 0 ? Math.min(1, Math.max(0, (heldCount - DOM_THRESHOLD) / denom)) : 1;
  return WIN.dominationHoldFloor + (WIN.dominationHoldMax - WIN.dominationHoldFloor) * (1 - overshoot);
}

export function tickClocks(state: GameState, dt: number): void {
  const c = state.clocks;
  c.sim += dt;
  // NOTE: the bot threat clock now lives per-bot in BotMemory and is decremented in sim.ts's update()
  // at the clocks phase (see sim.ts). state.clocks.botThreatTimer is mirrored there for legacy readers.

  // ---- Great Oxygenation ----
  if (!c.oxyActive && c.sim >= CLOCKS.oxyIgniteAt) {
    c.oxyActive = true;
    c.oxyLevel = 0;
    pushEvent(state, { kind: 'oxyIgnite', side: 'you', at: c.sim });
  }
  if (c.oxyActive) {
    c.oxyLevel = Math.min(1, (c.sim - CLOCKS.oxyIgniteAt) / CLOCKS.oxyRampSeconds);
    for (const side of ['you', 'bot'] as Side[]) {
      const col = state.colonies[side];
      const home = nodeById(state, col.homeId);
      if (!home) continue;
      const share = ownedCapturable(state, side).length / CLOCKS.capturableNodeCount;
      if (isAerobic(col)) {
        col.res.energy += CLOCKS.oxyBonusPerSec * c.oxyLevel * share * dt;
      } else {
        let dps = CLOCKS.oxyBaseDps * (1 - share) * c.oxyLevel;
        if (c.sim > CLOCKS.oxyLateRampAt) {
          dps += ((c.sim - CLOCKS.oxyLateRampAt) / 60) * CLOCKS.oxyLateRampPerMin;
        }
        home.integrity = Math.max(0, home.integrity - dps * dt);
      }
    }
  }

  // ---- Domination hold accounting ----
  const heldYou = ownedCapturable(state, 'you').length;
  const heldBot = ownedCapturable(state, 'bot').length;
  c.domYou = heldYou >= DOM_THRESHOLD ? c.domYou + dt : 0;
  c.domBot = heldBot >= DOM_THRESHOLD ? c.domBot + dt : 0;

  // ---- Win / loss detection ----
  detectWin(state, heldYou, heldBot);
}

function detectWin(state: GameState, heldYou: number, heldBot: number): void {
  if (state.result.over) return;
  const c = state.clocks;

  const youHome = nodeById(state, state.colonies.you.homeId);
  const botHome = nodeById(state, state.colonies.bot.homeId);

  // Conquest / Oxygenation (a Home reduced to 0 integrity).
  const reason = c.oxyActive ? 'oxygenation' : 'conquest';
  if (botHome && botHome.integrity <= 0) return setResult(state, 'you', reason);
  if (youHome && youHome.integrity <= 0) return setResult(state, 'bot', reason);

  // Domination.
  if (c.domYou >= domRequired(heldYou)) return setResult(state, 'you', 'domination');
  if (c.domBot >= domRequired(heldBot)) return setResult(state, 'bot', 'domination');
}

function setResult(state: GameState, winner: Side, reason: string): void {
  state.result.over = true;
  state.result.winner = winner;
  state.result.reason = reason;
}
